/**
 * PHARMAKON — barcodes · product (box) barcodes (A-08, spec §G, 03_DATABASE DB-11; refonte WP-12).
 *
 * A product carries the codes printed on its box: EAN-13, EAN-8, UPC-A, Code 128, and a GS1
 * DataMatrix whose GTIN (application identifier 01) is extracted. One normaliser / validator for
 * the server (product write path, exact resolver, CHIFA lookup, back-fill) and the SPA scan
 * surfaces: `analyserCodeProduit`; one equivalence rule for lookups: `formesEquivalentes`.
 *
 * Normalisation (DB-11): surrounding and inner whitespace removed, NEVER truncated, no case
 * folding (Code 128 is case-sensitive). Symbology: digits only, 8 / 12 / 13 / 14 long → EAN-8 /
 * UPC-A / EAN-13 / GTIN-14 with the GS1 check digit verified (a wrong one is a misread, UX M-S2-05);
 * any other printable-ASCII text → Code 128 (no check digit). A caller may declare `CODE128` to
 * keep an internal digits-only code whose last digit is not a GS1 check digit.
 *
 * GS1 equivalence: a GTIN keeps its check digit when left-padded with zeros, so EAN-13
 * 3400930000000, its DataMatrix GTIN 03400930000000, a UPC-A 036000291452 and its EAN-13 form
 * 0036000291452 designate the same item. Lookups compare every equivalent form.
 *
 * Canonical source: packages/shared/barcode/ (copy in apps/server/src/utils/barcode/, see gs1.js).
 */
import { estCleGs1Valide } from "./gs1.js";

/** The symbologies of a product barcode — the Prisma enum `SymbologieCodeBarre` (parity-tested). */
export const SYMBOLOGIE_CODE_PRODUIT = Object.freeze({
  EAN13: "EAN13",
  EAN8: "EAN8",
  UPCA: "UPCA",
  CODE128: "CODE128",
  GTIN14: "GTIN14",
});

/** Data shape of a stored product barcode (ProduitCodeBarre.code is VarChar(32)). */
export const CODE_PRODUIT_FORMAT = Object.freeze({
  LONGUEUR_MAX: 32,
  /** GS1 lengths of a digits-only code, by length. */
  LONGUEURS_GS1: Object.freeze({ 8: "EAN8", 12: "UPCA", 13: "EAN13", 14: "GTIN14" }),
  /** Longest GTIN (GTIN-14); every equivalent form is this one left-trimmed of zeros. */
  LONGUEUR_GTIN: 14,
});

/** Why a scanned or typed text is not a usable product barcode. */
export const RAISON_CODE_PRODUIT = Object.freeze({
  /** Nothing left after whitespace removal. */
  VIDE: "VIDE",
  /** EAN / UPC / GTIN shape with a wrong check digit — a misread (UX M-S2-05). */
  CLE_INVALIDE: "CLE_INVALIDE",
  /** A character outside printable ASCII (accents, control characters). */
  CARACTERES_INVALIDES: "CARACTERES_INVALIDES",
  /** Longer than `CODE_PRODUIT_FORMAT.LONGUEUR_MAX` — refused, never truncated (AUD-BARC-16). */
  TROP_LONG: "TROP_LONG",
  /** The declared symbology does not match the code (e.g. EAN13 declared for letters). */
  SYMBOLOGIE_INCOMPATIBLE: "SYMBOLOGIE_INCOMPATIBLE",
  /** A GS1 element string (DataMatrix) without a GTIN (no application identifier 01). */
  GS1_SANS_GTIN: "GS1_SANS_GTIN",
});

/** Where the code came from: typed / linear scan, or a GS1 element string (DataMatrix). */
export const SOURCE_CODE_PRODUIT = Object.freeze({ SAISIE: "SAISIE", GS1: "GS1" });

const GS = "\u001d"; // FNC1 as transmitted by scanners (ASCII group separator)
/** AIM symbology identifiers of the GS1 carriers (GS1-128, DataBar, DataMatrix, QR, DotCode). */
const IDENTIFIANTS_GS1 = ["]C1", "]e0", "]d2", "]Q3", "]J1"];
/**
 * GS1 « predefined length » table (General Specifications Figure 7.8.4-2): total length of the
 * element (AI included) by its first two digits; every other AI is variable and ends at FNC1.
 */
const LONGUEUR_PREDEFINIE = Object.freeze({
  "00": 20, "01": 16, "02": 16, "03": 16, "04": 18,
  11: 8, 12: 8, 13: 8, 14: 8, 15: 8, 16: 8, 17: 8, 18: 8, 19: 8,
  20: 4, 31: 10, 32: 10, 33: 10, 34: 10, 35: 10, 36: 10, 41: 16,
});
/** First two digits of the AIs a pharmaceutical DataMatrix carries after the GTIN (expiry, batch, serial, NHRN…). */
const SUITES_GS1 = /^(1[0-7]|2[0-2]|24|25|30|37|7[01]|9\d)/;
const ASCII_IMPRIMABLE = /^[\x21-\x7e]+$/;
const CHIFFRES = /^\d+$/;

/**
 * The GTIN of a GS1 element string, if `texte` is one.
 *   · with an AIM symbology identifier (]d2 DataMatrix, ]Q3 QR, ]C1 GS1-128…), or
 *   · in human-readable form « (01)03400930000000(17)270131(10)AB12 », or
 *   · raw: « 01 » + a valid GTIN-14, alone or followed by another pharmaceutical AI.
 *
 * @param {string} texte
 * @returns {{ gs1: false } | { gs1: true, gtin: string|null }}
 */
export function extraireGtinGs1(texte) {
  if (typeof texte !== "string") return { gs1: false };
  let s = texte.trim();
  const identifiant = IDENTIFIANTS_GS1.find((p) => s.startsWith(p));
  if (identifiant) s = s.slice(identifiant.length);

  if (s.startsWith("(")) {
    if (!/^\(\d{2,4}\)/.test(s)) return { gs1: false };
    const m = /\(01\)(\d{14})(?!\d)/.exec(s);
    return { gs1: true, gtin: m ? m[1] : null };
  }

  if (!identifiant) {
    const brut = s.startsWith(GS) ? s.slice(1) : s;
    const m = /^01(\d{14})/.exec(brut);
    if (!m || !estCleGs1Valide(m[1])) return { gs1: false };
    const suite = brut.slice(16).replace(new RegExp(`^${GS}`), "");
    if (suite.length > 0 && !SUITES_GS1.test(suite)) return { gs1: false };
    return { gs1: true, gtin: m[1] };
  }

  // AIM-identified element string: walk the elements (fixed lengths, else up to FNC1).
  let p = s.startsWith(GS) ? 1 : 0;
  while (p < s.length) {
    const ai2 = s.slice(p, p + 2);
    if (!/^\d{2}$/.test(ai2)) return { gs1: true, gtin: null };
    const longueur = LONGUEUR_PREDEFINIE[ai2];
    if (ai2 === "01") {
      const gtin = s.slice(p + 2, p + 16);
      return { gs1: true, gtin: /^\d{14}$/.test(gtin) ? gtin : null };
    }
    if (longueur) {
      p += longueur;
      if (s[p] === GS) p += 1;
    } else {
      const fin = s.indexOf(GS, p);
      if (fin < 0) break;
      p = fin + 1;
    }
  }
  return { gs1: true, gtin: null };
}

function refus(raison, code) {
  return { valide: false, raison, code };
}

/**
 * Normalises and validates a product barcode as scanned or typed.
 *
 * @param {string} texte
 * @param {{ symbologie?: string }} [options] a declared symbology (SYMBOLOGIE_CODE_PRODUIT); CODE128
 *   keeps the text as typed (no GS1 extraction, no check-digit verification: an internal code);
 *   a GS1 element string ignores any other declared symbology (its GTIN decides)
 * @returns {{ valide: true, code: string, symbologie: string, gtin: string|null, source: string }
 *         | { valide: false, raison: string, code: string }}
 *   `gtin` = the GTIN-14 of a GS1 code (null for Code 128); `code` = the stored form (a DataMatrix
 *   GTIN with indicator 0 is stored as the EAN-13 printed on the box)
 */
export function analyserCodeProduit(texte, { symbologie } = {}) {
  if (symbologie !== undefined && symbologie !== null && !Object.hasOwn(SYMBOLOGIE_CODE_PRODUIT, symbologie)) {
    return refus(RAISON_CODE_PRODUIT.SYMBOLOGIE_INCOMPATIBLE, typeof texte === "string" ? texte.trim() : "");
  }
  const brut = typeof texte === "string" ? texte : "";

  // A declared Code 128 is kept as typed: no GS1 extraction.
  const gs1 = symbologie === SYMBOLOGIE_CODE_PRODUIT.CODE128 ? { gs1: false } : extraireGtinGs1(brut);
  if (gs1.gs1) {
    const trace = brut.trim();
    if (!gs1.gtin) return refus(RAISON_CODE_PRODUIT.GS1_SANS_GTIN, trace);
    if (!estCleGs1Valide(gs1.gtin)) return refus(RAISON_CODE_PRODUIT.CLE_INVALIDE, gs1.gtin);
    const code = gs1.gtin.startsWith("0") ? gs1.gtin.slice(1) : gs1.gtin;
    return {
      valide: true,
      code,
      symbologie: code.length === 13 ? SYMBOLOGIE_CODE_PRODUIT.EAN13 : SYMBOLOGIE_CODE_PRODUIT.GTIN14,
      gtin: gs1.gtin,
      source: SOURCE_CODE_PRODUIT.GS1,
    };
  }

  const code = brut.replace(/\s+/g, "");
  if (code === "") return refus(RAISON_CODE_PRODUIT.VIDE, code);
  if (code.length > CODE_PRODUIT_FORMAT.LONGUEUR_MAX) return refus(RAISON_CODE_PRODUIT.TROP_LONG, code);
  if (!ASCII_IMPRIMABLE.test(code)) return refus(RAISON_CODE_PRODUIT.CARACTERES_INVALIDES, code);

  if (symbologie === SYMBOLOGIE_CODE_PRODUIT.CODE128) {
    return { valide: true, code, symbologie, gtin: null, source: SOURCE_CODE_PRODUIT.SAISIE };
  }
  const gs1Longueur = CHIFFRES.test(code) ? CODE_PRODUIT_FORMAT.LONGUEURS_GS1[code.length] : undefined;
  if (gs1Longueur) {
    if (!estCleGs1Valide(code)) return refus(RAISON_CODE_PRODUIT.CLE_INVALIDE, code);
    if (symbologie && symbologie !== gs1Longueur) return refus(RAISON_CODE_PRODUIT.SYMBOLOGIE_INCOMPATIBLE, code);
    return {
      valide: true,
      code,
      symbologie: gs1Longueur,
      gtin: code.padStart(CODE_PRODUIT_FORMAT.LONGUEUR_GTIN, "0"),
      source: SOURCE_CODE_PRODUIT.SAISIE,
    };
  }
  if (symbologie) return refus(RAISON_CODE_PRODUIT.SYMBOLOGIE_INCOMPATIBLE, code);
  return { valide: true, code, symbologie: SYMBOLOGIE_CODE_PRODUIT.CODE128, gtin: null, source: SOURCE_CODE_PRODUIT.SAISIE };
}

/**
 * Every stored form that designates the same item as `code` (a validated, stored-form code):
 * for a GS1 code its GTIN-14 and each shorter form obtained by dropping leading zeros down to
 * 13, 12 and 8 digits; for a Code 128 the code itself (exact, case-sensitive).
 *
 * @param {string} code
 * @param {string} [symbologie]
 * @returns {string[]} distinct forms, longest first
 */
export function formesEquivalentes(code, symbologie) {
  if (typeof code !== "string" || code === "") return [];
  const gs1 = symbologie !== SYMBOLOGIE_CODE_PRODUIT.CODE128
    && CHIFFRES.test(code)
    && CODE_PRODUIT_FORMAT.LONGUEURS_GS1[code.length]
    && estCleGs1Valide(code);
  if (!gs1) return [code];
  const gtin = code.padStart(CODE_PRODUIT_FORMAT.LONGUEUR_GTIN, "0");
  const formes = [];
  for (const n of [14, 13, 12, 8]) {
    const zeros = gtin.slice(0, CODE_PRODUIT_FORMAT.LONGUEUR_GTIN - n);
    if (/^0*$/.test(zeros)) formes.push(gtin.slice(CODE_PRODUIT_FORMAT.LONGUEUR_GTIN - n));
  }
  return [...new Set(formes)];
}

/**
 * The comparison key of a stored-form code: its GTIN-14 for a GS1 code, else the code itself.
 * Two codes designate the same item exactly when their keys are equal.
 */
export function cleComparaisonCode(code, symbologie) {
  const [premiere] = formesEquivalentes(code, symbologie);
  return premiere ?? "";
}
